import { lazy, Suspense, useCallback, useLayoutEffect, useMemo, useState } from "react";
import { AppShell } from "./components/AppShell";
import { AppMarketingPage } from "./components/AppMarketingPage";
import { AppFeatureShowcase } from "./components/AppFeatureShowcase";
import { ContentSection } from "./components/ContentSection";
import { ContactPage } from "./components/ContactPage";
import { Footer } from "./components/Footer";
import { Hero } from "./components/Hero";
import { LeadForm } from "./components/LeadForm";
import { LegalDocument } from "./components/LegalDocument";
import { RelatedLinks } from "./components/RelatedLinks";
import { Seo } from "./components/Seo";
import { SiteHeader } from "./components/SiteHeader";
import { useRoute } from "./hooks/useRoute";
import HkDevPage from "./HkDevPage";
import { getPage } from "./siteContent";
import { resetScrollAfterRender } from "./utils/scrollReset";
import "./App.css";
import "./FinalTheme.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://aspirant-backend.vercel.app";
const BlogIndex = lazy(() => import("./components/BlogPages").then((module) => ({ default: module.BlogIndex })));
const BlogArticle = lazy(() => import("./components/BlogPages").then((module) => ({ default: module.BlogArticle })));
const CssSubjectSyllabusPage = lazy(() => import("./components/CssSubjectSyllabusPage").then((module) => ({ default: module.CssSubjectSyllabusPage })));
const CssSyllabusDirectory = lazy(() => import("./components/CssSubjectSyllabusPage").then((module) => ({ default: module.CssSyllabusDirectory })));
const MptResultPage = lazy(() => import("./components/MptResultPage").then((module) => ({ default: module.MptResultPage })));
const AnswerWritingTipsPage = lazy(() => import("./components/AnswerWritingTipsPage").then((module) => ({ default: module.AnswerWritingTipsPage })));

function BlogLoadingState() {
  return (
    <section className="content-block" aria-live="polite">
      <span className="section-eyebrow">Loading guide</span>
      <h2>Preparing the reading desk…</h2>
    </section>
  );
}

function App() {
  const { path, navigate } = useRoute();
  const page = useMemo(() => getPage(path), [path]);

  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  useLayoutEffect(() => {
    return resetScrollAfterRender();
  }, [path]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const trimmedEmail = email.trim().toLowerCase();

      if (!trimmedEmail) {
        setStatus("error");
        setMessage("Please enter your email address.");
        return;
      }

      try {
        setStatus("loading");
        setMessage("");

        const response = await fetch(`${API_BASE_URL}/api/waitlist`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: trimmedEmail,
            whatsapp: whatsapp.trim(),
            note: note.trim(),
            source: "aspirant-frontend"
          })
        });

        const rawBody = await response.text();
        let result = {};
        if (rawBody) {
          try {
            result = JSON.parse(rawBody);
          } catch (_error) {
            result = {};
          }
        }

        if (!response.ok || !result.ok) {
          throw new Error(result.message || "Unable to submit right now.");
        }

        setStatus("success");
        setMessage("Thanks. Your note is saved.");
        setEmail("");
        setWhatsapp("");
        setNote("");
      } catch (error) {
        setStatus("error");
        setMessage(error.message || "Submission failed. Please try again.");
      }
    },
    [email, note, whatsapp]
  );

  if (path === "/hk/dev") {
    return (
      <div className="shell">
        <Seo page={page} path={path} />
        <div className="site-shell">
          <SiteHeader navigate={navigate} path={path} />
          <HkDevPage apiBaseUrl={API_BASE_URL} />
          <Footer navigate={navigate} />
        </div>
      </div>
    );
  }

  const showForm = page.kind === "home";
  const isContactPage = page.kind === "contact";
  const isBlogIndex = page.kind === "blogs";
  const isBlogArticle = page.kind === "blog";
  const isLegalDocument = page.kind === "legal";
  const isAppMarketingPage = page.kind === "app";
  const isSubjectSyllabus = page.kind === "subject-syllabus";
  const isMptResult = page.kind === "mpt-result";
  const isAnswerTip = page.kind === "answer-tip";
  const showHero = !isLegalDocument && !isContactPage && !isAppMarketingPage && !isBlogIndex && !isBlogArticle && !isSubjectSyllabus && !isMptResult && !isAnswerTip;
  const homeSections = showForm
    ? page.sections?.filter((section) => section.type !== "appFeatureShowcase") || []
    : [];
  let homeCardCount = 0;
  const homeCardOffsets = homeSections.map((section) => {
    const offset = homeCardCount;
    homeCardCount += section.cards?.length || 0;
    return offset;
  });

  return (
    <AppShell page={page} path={path} navigate={navigate}>
      <main className="page" key={path}>
        {showHero ? <Hero page={page} navigate={navigate} /> : null}

        {isLegalDocument ? (
          <LegalDocument page={page} />
        ) : isSubjectSyllabus ? (
          <Suspense fallback={<BlogLoadingState />}>
            <CssSubjectSyllabusPage page={page} navigate={navigate} />
          </Suspense>
        ) : isMptResult ? (
          <Suspense fallback={<BlogLoadingState />}>
            <MptResultPage navigate={navigate} />
          </Suspense>
        ) : isAnswerTip ? (
          <Suspense fallback={<BlogLoadingState />}>
            <AnswerWritingTipsPage navigate={navigate} />
          </Suspense>
        ) : isBlogIndex ? (
          <Suspense fallback={<BlogLoadingState />}>
            <BlogIndex navigate={navigate} />
          </Suspense>
        ) : isBlogArticle ? (
          <Suspense fallback={<BlogLoadingState />}>
            <BlogArticle page={page} navigate={navigate} />
          </Suspense>
        ) : isContactPage ? (
          <ContactPage />
        ) : isAppMarketingPage ? (
          <AppMarketingPage page={page} navigate={navigate} />
        ) : showForm ? (
          <>
            <AppFeatureShowcase navigate={navigate} />
            <div className="page-grid">
              <div className="page-main">
                {homeSections.map((section, index) => (
                  <ContentSection key={section.title} section={section} navigate={navigate} index={index} cardOffset={homeCardOffsets[index]} showMobileDownloadPrompts />
                ))}
              </div>
              <div className="page-side">
                <LeadForm
                  email={email}
                  whatsapp={whatsapp}
                  note={note}
                  status={status}
                  message={message}
                  setEmail={setEmail}
                  setWhatsapp={setWhatsapp}
                  setNote={setNote}
                  onSubmit={handleSubmit}
                />
                <RelatedLinks related={page.related} navigate={navigate} title="Useful routes" />
              </div>
            </div>
          </>
        ) : (
          <>
            {path === "/css-syllabus" || path === "/css-subjects" ? (
              <Suspense fallback={<BlogLoadingState />}>
                <CssSyllabusDirectory navigate={navigate} />
              </Suspense>
            ) : null}
            {page.sections?.map((section, index) => (
              <ContentSection key={section.title} section={section} navigate={navigate} index={index} />
            ))}
            <RelatedLinks related={page.related} navigate={navigate} title="Related pages" />
          </>
        )}
      </main>
    </AppShell>
  );
}

export default App;
